<script>
export default {
    name: 'ZhMultiselect',

    props: {
        simpleOptions: {
            type: Boolean,
            default: false
        },
        options: {
            type: Array,
            required: true
        },
        initSelected: {
            default: () => null,
            validator: function () {
                return true
            }
        },
        placeholder: {
            type: String,
            required: true
        },
    },

    data() {
        return {
            selected: null
        }
    },

    watch: {
        initSelected: {
            handler(newValue) {
                this.selected = newValue
            }
        }
    },

    created() {
        if ( this.initSelected ) {
            this.selected = this.initSelected
        }
    }
}
</script>

<template>
    <div>
        <!-- ARRAY OF OPTIONS AS OBJECT BELOW -->
        <multiselect
            v-if="!simpleOptions"
            v-model="selected"
            :options="options"
            :placeholder="placeholder"
            track-by="id"
            label="name"
            select-label=""
            deselect-label=""
            selected-label=""
            multiple
            :searchable="false"
            :show-labels="false"
            :option-height="30"
            :max-height="233"
            :limit="3"
            :limit-text="count => `és további ${count}`"
            v-on="$listeners"
        >
            <span
                slot="caret"
                class="arrow"
            >
                <span>
                    <svg class="icon icon-cal-select-down">
                        <use xlink:href="#cal-select-down" />
                    </svg>
                </span>
            </span>
            <template
                slot="option"
                slot-scope="props"
            >
                <div class="unselect-space">
                    <svg class="icon icon-cal-select-unselect">
                        <use xlink:href="#cal-select-unselect" />
                    </svg>
                </div>
                <div class="option__desc">
                    <span class="option__title">{{ props.option.name }}</span>
                </div>
            </template>
        </multiselect>
        <!-- SIMPLE ARRAY OPTIONS BELOW -->
        <multiselect
            v-else
            v-model="selected"
            :options="options"
            :placeholder="placeholder"
            track-by="name"
            label="name"
            select-label=""
            deselect-label=""
            selected-label=""
            multiple
            :searchable="false"
            :show-labels="false"
            :option-height="30"
            :max-height="233"
            :limit="3"
            :limit-text="count => `és további ${count}`"
            v-on="$listeners"
        >
            <span
                slot="caret"
                class="arrow"
            >
                <span>
                    <svg class="icon icon-cal-select-down">
                        <use xlink:href="#cal-select-down" />
                    </svg>
                </span>
            </span>
            <template
                slot="option"
                slot-scope="props"
            >
                <div class="unselect-space">
                    <svg class="icon icon-cal-select-unselect">
                        <use xlink:href="#cal-select-unselect" />
                    </svg>
                </div>
                <div class="option__desc">
                    <span class="option__title">{{ props.option.name }}</span>
                </div>
            </template>
        </multiselect>
    </div>
</template>

<style lang="scss" scoped>
@import "./resources/sass/_variables.scss";

// MULTISELECT BELOW
.arrow {
    position: absolute;
    z-index: 45;
    right: 14px;
    top: 6px;
}

::v-deep .multiselect__input {
    color: #fff;
    font-size: 12px;
    position: relative;
    display: inline-block;
    min-height: 20px;
    line-height: 20px;
    border: none;
    border-radius: 5px;
    background: transparent;
    padding: 0 0 0 5px;
    width: 100%;
    transition: border .1s ease;
    box-sizing: border-box;
    margin-bottom: 0;
    vertical-align: top;

    &:focus::placeholder {
        color: #ffffff4d;
        font-size: 12px;
    }
}

::v-deep .multiselect__content {
    padding-top: 8px;
    color: $zh-blue;
}

::v-deep .multiselect__content-wrapper {
    margin-top: -8px;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    width: calc( 100% - 1rem );
    position: absolute;
    display: block;
    background: $zh-suplementary-selected;
    overflow: auto;
    border: 0;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    z-index: 30;

    &::-webkit-scrollbar {
        width: 11px;
    }

    scrollbar-width: thin;
    scrollbar-color: var(--thumbBG) var(--scrollbarBG);

    &::-webkit-scrollbar-track {
        background: var(--scrollbarBG);
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--thumbBG) ;
        border-radius: 6px;
        border: 3px solid var(--scrollbarBG);
    }
}

::v-deep .multiselect--active:not(.multiselect--above) .multiselect__input {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

::v-deep .multiselect--above.multiselect--active .multiselect__tags {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
}

::v-deep .multiselect__placeholder {
    color: #fff;
    padding-top: 0;
    padding-left: 6px;
    font-size: 12px;
}

::v-deep .multiselect__tags {
    position: relative;
    z-index: 40;

    // max-height: 40px;
    background: $zh-blue;
    border-radius: 20px;
    border-color: #ffffff4d;
}

::v-deep .multiselect--active {
    // z-index: unset;
}

::v-deep .multiselect--active:not(.multiselect-above) .multiselect__tags {
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
}

::v-deep .multiselect__element {
    font-size: 14px;

    &> span {
        display: flex;
        padding: 7px 12px 7px 8px;
        min-height: 30px;
    }
}

::v-deep .multiselect__option--highlight {
    color: $zh-blue;
    background-color: $zh-suplementary-inactive;
}

::v-deep .multiselect__option {
    & .icon-cal-select-unselect {
        display: none;
    }
}

::v-deep .multiselect__option--selected {
    color: $zh-blue;
    background-color: $zh-suplementary-inactive;
    font-weight: normal;

    & .icon-cal-select-unselect {
        display: inline !important;
    }
}

::v-deep .multiselect__tag {
    color: $zh-blue;
    background-color: #fff;
    border-radius: 12px;
    margin-bottom: 2px;

    &> .multiselect__tag-icon {
        &:focus,
        &:hover {
            background-color: $zh-suplementary-inactive;

            &:after {
                color: $zh-blue;
            }
        }
    }
}

::v-deep .multiselect__strong {
    color: #fff;
    font-weight: normal;
    font-size: 12px;
    line-height: 24px;
    margin-bottom: 5px;
}

::v-deep .option__desc {
    padding-top: 2px;
    padding-left: 0.5rem;
}

.unselect-space {
    min-width: 20px;
    width: 20px;
}

</style>
